import {
  LEAD_SEARCH_WINDOW_DAYS,
  MIN_SEARCH_RUNES,
  SEARCH_WINDOW_DAYS,
} from './MessageSearch.constants';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Runes (Unicode code points), not `String.length`: `length` counts UTF-16
 * units, so an astral-plane character (most emoji) would count as two.
 */
export function countRunes(value: string): number {
  return Array.from(value).length;
}

export function normalizeSearchTerm(value: string): string {
  return value.trim();
}

/**
 * Searchable means at least MIN_SEARCH_RUNES code points after `trim()`. No
 * exemption for emoji or punctuation: they produce no full-text lexemes, so a
 * query made only of them can never match. Mirrors: bff-web
 * `messaging.constants.go` (`MinSearchQueryRunes`) and messaging
 * `MessageSearcher.ts` (`MIN_QUERY_RUNES`, authoritative: the only layer that
 * answers 400). Change all three together.
 */
export function isSearchTermLongEnough(term: string): boolean {
  return countRunes(normalizeSearchTerm(term)) >= MIN_SEARCH_RUNES;
}

const WORD_CHARACTER = /[\p{L}\p{N}]/u;

/**
 * True when the query only excludes words and never asks for one.
 *
 * bff-web hands the text to `websearch_to_tsquery`, where `-word` means "not
 * word" and a bare `or` is an operator. A query made only of exclusions
 * (`-precio`, `-precio or -envio`) has no positive key for the GIN index to
 * look up, so Postgres answers it by reading every message in the window — the
 * one shape of this search that is expensive by construction. The inbox box
 * searches on every pause in typing, so it must not send it.
 *
 * Deliberately narrow: a query with no exclusion at all is never flagged here,
 * whatever it contains, so anything this returns false for is sent exactly as
 * it was before the check existed.
 *
 * @param term - The trimmed query.
 * @returns Whether the query has at least one exclusion and no positive term.
 */
export function isNegationOnlyQuery(term: string): boolean {
  let hasExclusion = false;

  for (const rawToken of term.split(/\s+/)) {
    const token = rawToken.replace(/^"+/, '');

    if (!token || token.toLowerCase() === 'or') continue;

    if (token.startsWith('-')) {
      // A lone "-" is punctuation, not an operator: websearch_to_tsquery only
      // negates when the minus is glued to a word.
      if (WORD_CHARACTER.test(token.slice(1))) hasExclusion = true;

      continue;
    }

    return false;
  }

  return hasExclusion;
}

/**
 * The `since` floor for a new search, as an ISO 8601 string.
 *
 * Computed once per search and reused for every page of that search's cursor
 * chain: recomputing it per page would move the window under the keyset and
 * silently change which rows a later page can see.
 */
export function defaultSearchSince(now: Date = new Date()): string {
  return new Date(
    now.getTime() - SEARCH_WINDOW_DAYS * MS_PER_DAY,
  ).toISOString();
}

/**
 * The `since` floor for a new in-chat search (one contact), as ISO 8601.
 *
 * Same freezing rule as defaultSearchSince: computed once per search and reused
 * for its whole cursor chain.
 */
export function defaultLeadSearchSince(now: Date = new Date()): string {
  return new Date(
    now.getTime() - LEAD_SEARCH_WINDOW_DAYS * MS_PER_DAY,
  ).toISOString();
}
